// src/GameContext.tsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { collection, onSnapshot, getDocs } from "firebase/firestore";
import { db1 } from "../Firebase/firebase1";
import { db2 } from "../Firebase/firebase2";
import { db3 } from "../Firebase/firebase3";
import { db4 } from "../Firebase/firebase4";
import { db5 } from "../Firebase/firebase5";
import { db6 } from "../Firebase/firebase6";
import { db7 } from "../Firebase/firebase7";
import { db8 } from "../Firebase/firebase8";
import { db9 } from "../Firebase/firebase9";
import { GameSession } from "../types";

interface GameContextType {
    gameData: { [key: string]: GameSession[] };
    refreshGameData: (gameKey: string) => Promise<void>;
    lastRefreshed: { [key: string]: Date | null };
    isLoading: { [key: string]: boolean };
}

const GameContext = createContext<GameContextType | undefined>(undefined);

// Key for storing last refresh timestamps in localStorage
const LAST_REFRESH_KEY = 'firescore_last_refresh';

export const GameProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [gameData, setGameData] = useState<{ [key: string]: GameSession[] }>({
        game1: [],
        game2: [],
        game3: [],
        game4: [],
        game5: [],
        game6: [],
        game7: [],
        game8: [],  
        game9: [],
    });
    const [lastRefreshed, setLastRefreshed] = useState<{ [key: string]: Date | null }>({
        game1: null,
        game2: null,
        game3: null,
        game4: null,
        game5: null,
        game6: null,
        game7: null,
        game8: null,
        game9: null,
    });
    const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({
        game1: false,
        game2: false,
        game3: false,
        game4: false,
        game5: false,
        game6: false,
        game7: false,
        game8: false,
        game9: false,
    });

    // Function to fetch data for a specific game
    const fetchGameData = async (gameKey: string) => {
        setIsLoading(prev => ({ ...prev, [gameKey]: true }));
        const dbIndex = parseInt(gameKey.replace('game', '')) - 1;
        const dbs = [db1, db2, db3, db4, db5, db6, db7, db8, db9];
        const db = dbs[dbIndex];

        try {
            const querySnapshot = await getDocs(collection(db, "users"));
            const sessions = querySnapshot.docs.map((doc) => {
                const data = doc.data();
                return {
                    id: doc.id,
                    userId: data.userId,
                    confirmClicked: data.confirmClicked,
                    maxLevel: data.maxLevel,
                    purchaseClicked: data.purchaseClicked,
                    sessionTime: data.sessionTime,
                    registrationDate: data.registrationDate,
                    userIdTest: data.userIdTest,
                    
                    // Echoes of Self specific fields
                    CanvasQuestion: data.CanvasQuestion,
                    FoodItemController: data.FoodItemController,
                    GhostMan: data.GhostMan,
                    ImageQuestion: data.ImageQuestion,
                    Inventory: data.Inventory,
                    LampMan: data.LampMan,
                    MemoryManager: data.MemoryManager,
                    PlayerPositions: data.PlayerPositions,
                    QuiltMan: data.QuiltMan,
                    DiceGame: data.DiceGame,
                    ColorClickData: data.ColorClickData,
                    Violin: data.Violin,
                    InventoryMBKIComparator: data.InventoryMBKIComparator,

                    // Frostville specific fields
                    AvatarStats: data.AvatarStats,
                    DoorGameStats: data.DoorGameStats,
                    FishGameStats: data.FishGameStats,
                    IceGameStats: data.IceGameStats,
                    PrisonersDilemmaGameStats: data.PrisonersDilemmaGameStats,

                    // Blockhop specific fields
                    GameData: data.GameData,
                    
                    // Other games' fields
                    horoscopeData: data.horoscopeData,
                    HoroscopeInfo: data.HoroscopeInfo,
                    EvaluateYourself: data.EvaluateYourself,
                    LineJudgement: data.LineJudgement,
                    MatchToSimpleGameManager: data.MatchToSimpleGameManager,
                    MathTest: data.MathTest,
                    Sudoku: data.Sudoku,
                    HoroscopeList: data.HoroscopeList,
                    Stroop: data.Stroop,
                    WordTest: data.WordTest,
                    CorsiBlock: data.CorsiBlock,
                } as GameSession;
            });

            setGameData(prev => ({ ...prev, [gameKey]: sessions }));
            const now = new Date();
            setLastRefreshed(prev => ({ ...prev, [gameKey]: now }));
            
            // Save refresh time to localStorage
            try {
                const savedRefreshTimes = JSON.parse(localStorage.getItem(LAST_REFRESH_KEY) || '{}');
                savedRefreshTimes[gameKey] = now.toISOString();
                localStorage.setItem(LAST_REFRESH_KEY, JSON.stringify(savedRefreshTimes));
            } catch (error) {
                console.error("Error saving refresh time to localStorage:", error);
            }
            
            // Save game data to localStorage
            try {
                const savedGameData = JSON.parse(localStorage.getItem('firescore_gameData') || '{}');
                savedGameData[gameKey] = sessions;
                localStorage.setItem('firescore_gameData', JSON.stringify(savedGameData));
            } catch (error) {
                console.error("Error saving game data to localStorage:", error);
            }
            
        } catch (error) {
            console.error(`Error fetching data for ${gameKey}:`, error);
        } finally {
            setIsLoading(prev => ({ ...prev, [gameKey]: false }));
        }
    };

    // Function to refresh data for a specific game
    const refreshGameData = async (gameKey: string) => {
        await fetchGameData(gameKey);
    };
    
    // Check localStorage on load and set up initial data
    useEffect(() => {
        let savedRefreshTimes: { [key: string]: string } = {};
        let savedGameData: { [key: string]: GameSession[] } = {};
        
        try {
            const refreshTimesStr = localStorage.getItem(LAST_REFRESH_KEY);
            if (refreshTimesStr) {
                savedRefreshTimes = JSON.parse(refreshTimesStr);
            }
        } catch (error) {
            console.error("Error parsing refresh times from localStorage:", error);
            localStorage.removeItem(LAST_REFRESH_KEY);
        }
        
        try {
            const gameDataStr = localStorage.getItem('firescore_gameData');
            if (gameDataStr) {
                savedGameData = JSON.parse(gameDataStr);
            }
        } catch (error) {
            console.error("Error parsing game data from localStorage:", error);
            localStorage.removeItem('firescore_gameData');
        }
        
        // Set last refreshed times
        const initialLastRefreshed: { [key: string]: Date | null } = {};
        Object.keys(gameData).forEach(gameKey => {
            const refreshTime = savedRefreshTimes[gameKey];
            initialLastRefreshed[gameKey] = refreshTime ? new Date(refreshTime) : null;
        });
        setLastRefreshed(initialLastRefreshed);
        
        // Set game data
        const initialGameData: { [key: string]: GameSession[] } = {};
        Object.keys(gameData).forEach(gameKey => {
            if (savedGameData[gameKey] && Array.isArray(savedGameData[gameKey])) {
                initialGameData[gameKey] = savedGameData[gameKey];
            } else {
                // If no data exists for a game or data is invalid, fetch it
                fetchGameData(gameKey);
            }
        });
        setGameData(initialGameData);
    }, []);

    return (
        <GameContext.Provider value={{ gameData, refreshGameData, lastRefreshed, isLoading }}>
            {children}
        </GameContext.Provider>
    );
};

export const useGameData = () => {
    const context = useContext(GameContext);
    if (!context) {
        throw new Error("useGameData must be used within a GameProvider");
    }
    return context;
};